import clsx from 'clsx';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  DiscreteColorLegend,
  FlexibleXYPlot,
  HorizontalGridLines,
  LineSeries,
  VerticalGridLines,
  XAxis,
  YAxis,
} from 'react-vis';
import styles from './MLPanel.mod.scss';
import useBluetooth from '@utils/hooks/bluetooth';
import useMachineLearning from '@utils/hooks/machineLearning';
import { convertBase64ToObject, convertObjectToBase64 } from '@utils/hooks/machineLearning/util';
import LinearProgress from '@mui/material/LinearProgress';
import { MODEL_TYPES, TRAIN_STEPS, CONFIDENCE_THRESHOLD } from '@utils/hooks/machineLearning/params';
import { pl } from 'date-fns/locale';
import { Steps } from 'intro.js-react';
import { useBlockly } from '@modules/blockly/features/hooks';
import {
  setMLModel,
  setMLModelJson,
  setMLModelWeights,
  setMLMinSamplesRequired,
  setMLTrainingParams,
  setMLTrainingStep,
  setMLSamples,
  setMLIsClassifying,
  setMLIsTraining,
  setMLModelTrained,
  setMLPrediction,
  setMLActive,
  setMLSamplesModalActive,
  deleteMLTensors,
} from '@modules/blockly/features/blocklySlice';
import MLClassContainer from './machineLearning/MLClassContainer';
import MLSamplesDialog from './machineLearning/MLSamplesDialog';
import { set } from 'date-fns';
import { machine } from 'os';
import { Propane } from '@mui/icons-material';
import { Button } from '@mui/material';
import { ArrowRightIcon, IconBlocks, PadlockIcon, PlusIcon } from '@assets/icons';
import IconPanel from '@assets/icons/iconPanel';
import BlocksFill from '@assets/icons/blocksFill';
import PlayIcon from '@assets/icons/playIcon';
import CamaraAnalyze from '@assets/icons/camaraAnalyze';
import Bluetooth from '@assets/icons/bluetooth';
import BluetoothConected from '@assets/icons/bluetoothConected';
import ArrowIcon from '@assets/icons/arrow';
import GeminiIcon from '@assets/icons/geminiIcon';
import GeminiFill from '@assets/icons/geminiFill';
import * as routes from '@educabot/educablocks-cosmos';
import LockIcon from '@assets/icons/lockIcon';
import ArrowLeftIcon from '@assets/icons/arrowLeft';
import AdvancedTooltip from '../../../components/AdvancedTooltip';

const ARDUINO_CONNECTION_SERVICE = '0000ffe0-0000-1000-8000-00805f9b34fb';
const ARDUINO_CONNECTION_CHARACTERISTIC = '0000ffe1-0000-1000-8000-00805f9b34fb';

// const ARDUINO_CONNECTION_SERVICE = ['0000ffe0-0000-1000-8000-00805f9b34fb', '6e400001-b5a3-f393-e0a9-e50e24dcca9e']
// const ARDUINO_CONNECTION_NAMEPREFIXES = ['buty', 'EDUCABOT', 'EducaBot', 'educaBot']
const ARDUINO_CONNECTION_NAMEPREFIXES = null;
// const ARDUINO_CONNECTION_CHARACTERISTIC = null

const MLPanel = React.memo((props) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const DebounceState = useRef(0);
  const lastSentCommand = useRef('');
  const plotterData = useRef([]);
  const machineLearning = useRef({});

  const [askCommandClass, setAskCommandClass] = useState('GET_CLASS');
  const [commandClass, setCommandClass] = useState('MLClass');
  const [stopCommandClass, setStopCommandClass] = useState('MLC_Stop');
  const [mediaWrapper, setMediaWrapper] = useState('mediaWrapper');
  const [confidenceLevel, setConfidenceLevel] = useState(CONFIDENCE_THRESHOLD);
  const [messageDelay, setMessageDelay] = useState(300);
  const [savePredictionStateDelay, setSavePredictionStateDelay] = useState(500);
  const [cameraAllowed, setCameraAllowed] = useState(false);
  const [selectedTextClass, setSelectedTextClass] = useState('');
  const [selectedTextSamples, setSelectedTextSamples] = useState([]);
  const [confidences, setConfidences] = useState({});
  const [importedModel, setImportedModel] = useState(null);
  const [loading, setLoading] = useState(null);
  const [modelType, setModelType] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [modelTrained, setModelTrained] = useState(false);
  const [minSamplesRequired, setMinSamplesRequired] = useState({
    samples: 2,
    classes: 2,
  });
  const [trainingParams, setTrainingParams] = useState({
    epochs: 0,
    fineTunningEpochs: 0,
    learningRate: 0,
    batchSize: 0,
  });
  const [trainingData, setTrainingData] = useState({
    epoch: 0,
    acc: 0,
    loss: 0,
    val_acc: 0,
    val_loss: 0,
  });
  const [hasBluetooth, setHasBluetooth] = useState(props.board?.bluetooth || false);
  const [steps, setSteps] = useState(null);
  const [stepsEnabled, setStepsEnabled] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const blocklyState = useBlockly();
  const [showSamplesDialog, setShowSamplesDialog] = useState(false);
  const [currentSampleId, setCurrentSampleId] = useState(null);
  const [loadingSample, setLoadingSample] = useState(false);
  const [isCreabots, setIsCreabots] = useState(props.isCreabots || false);
  const [mlHandler, setMLHandler] = useState(null);

  const isFreeProject = props.isFreeProject || false;
  const codeUploaded = blocklyState?.build?.progress === 101 && !blocklyState?.build?.building;

  machineLearning.current = blocklyState?.machineLearning || {};
  // console.log('================machineLearning', machineLearning.current);

  const isClassifying = machineLearning.current.isClassifying || false;
  const trainingStep = machineLearning.current.trainingStep || TRAIN_STEPS.TEACH;
  const totalSamples = Object.keys(machineLearning.current.samples).length;

  const TOOLTIP_CONFIG = {
    AI_PANEL: {
      bluetooth_connected: {
        title: intl.formatMessage({ id: 'machineLearning.tooltips.ai.title' }),
        description: intl.formatMessage({ id: 'machineLearning.tooltips.ai.bluetoothConnected.description' }),
        tooltipId: 'mlpanel-code-uploaded-bluetooth-connected',
      },
      bluetooth_disconnected: {
        title: intl.formatMessage({ id: 'machineLearning.tooltips.ai.title' }),
        description: intl.formatMessage({ id: 'machineLearning.tooltips.ai.bluetoothDisconnected.description' }),
        tooltipId: 'mlpanel-code-uploaded-bluetooth-disconnected',
      },
    },
    BLOCKS_PANEL: {
      title: intl.formatMessage({ id: 'machineLearning.tooltips.blocks.title' }),
      description: intl.formatMessage({ id: 'machineLearning.tooltips.blocks.description' }),
      tooltipId: 'mlpanel-toggle-blocks',
    },
  };

  const getAIPanelTooltipConfig = () => {
    return connectedRef.current
      ? TOOLTIP_CONFIG.AI_PANEL.bluetooth_connected
      : TOOLTIP_CONFIG.AI_PANEL.bluetooth_disconnected;
  };

  const getAIPanelIcon = () => {
    return connectedRef.current ? (
      <GeminiFill width={14} height={14} />
    ) : (
      <Bluetooth width={14} height={14} color="#fff" />
    );
  };

  const visibleSamples = Object.values(machineLearning.current.samples).filter((sample) => sample.active);
  const allClassesHaveNames = visibleSamples.every(({ name }) => name && name.trim() !== '');
  const allSamplesHaveImages = visibleSamples.every(({ image }) => image.length >= minSamplesRequired.samples);
  const someSamplesHaveImages = visibleSamples.some(({ image }) => image.length > 0);
  const activeClasses = visibleSamples.filter((sample) => sample.active).length;
  const isTrainingDisabled =
    !allClassesHaveNames ||
    visibleSamples.length < minSamplesRequired.classes ||
    !allSamplesHaveImages ||
    activeClasses < minSamplesRequired.classes;

  const { requestConnection, disconnect, startNotifications, sendMessage, connectedRef, receivedMessageRef, clearMessage } = useBluetooth({
    serviceID: ARDUINO_CONNECTION_SERVICE,
    characteristicID: ARDUINO_CONNECTION_CHARACTERISTIC,
    namePrefix: ARDUINO_CONNECTION_NAMEPREFIXES,
  });

  const getPrediction = (detectionConfidences) => {
    setConfidences({ ...detectionConfidences });
    if (Date.now() - DebounceState.current > savePredictionStateDelay) {
      dispatch(setMLPrediction(detectionConfidences));
      DebounceState.current = Date.now();
    }
    let classDetected = false;
    if (Object.keys(detectionConfidences).length > 0) {
      for (const key of Object.keys(machineLearning.current.samples)) {
        const sample = machineLearning.current.samples[key];
        if (!sample.active) continue;
        const value = detectionConfidences[sample.name] || detectionConfidences[key] || 0;
        if (value * 100 >= confidenceLevel) {
          classDetected = true;
          if (
            connectedRef.current &&
            (lastSentCommand.current === '' || receivedMessageRef.current === askCommandClass)
          ) {
            const commandToSend = `${commandClass}${sample.index}`;
            console.log(
              '================message',
              commandToSend,
              (detectionConfidences[sample.name] || 0) * 100,
              sample.name
            );
            sendMessage(commandToSend);
            lastSentCommand.current = commandToSend;
            clearMessage();
          }
        }
      }
    }
    if (
      !classDetected &&
      connectedRef.current &&
      lastSentCommand.current !== stopCommandClass &&
      receivedMessageRef.current === askCommandClass
    ) {
      console.log('================message stop', stopCommandClass);
      sendMessage(stopCommandClass);
      lastSentCommand.current = stopCommandClass;
      clearMessage();
    }
  };
  const {
    init,
    getDefaultTrainingParams,
    getMinSamplesRequired,
    stopMedia,
    startMedia,
    addClass,
    editClassName,
    deleteClass,
    deleteSampleImages,
    addSample,
    getSamples,
    importSamples,
    clearAllSamples,
    startClassifying,
    stopClassifying,
    train,
    saveModelToDB,
    loadModelFromDB,
    loadModel,
  } = useMachineLearning({
    trainingParams,
    wrapperClass: mediaWrapper,
    getPredictionCallback: getPrediction,
  });

  const tutorialOptions = {
    options: {
      showButtons: false,
      showStepNumbers: false,
      // exitOnOverlayClick: false,
      // disableInteraction: true,
      showBullets: false,
      overlayOpacity: 0,
    },
    steps: [
      {
        element: '[aria-posinset="12"]',
        intro: intl.formatMessage({ id: 'machineLearning.tutorial.step0' }),
        position: 'right',
        tooltipClass: styles.stepsTooltipClass,
        highlightClass: styles.stepsHighlightClass,
        hintAnimation: true,
      },
      {
        element: `[role="blocklyBuild"]`,
        intro: intl.formatMessage({ id: 'machineLearning.tutorial.step1' }),
        position: 'top',
        tooltipClass: styles.stepsTooltipClass,
        highlightClass: styles.stepsHighlight2Class,
      },
      {
        element: '#MLConnectBluetooth',
        intro: intl.formatMessage({ id: 'machineLearning.tutorial.step2' }),
        position: 'top',
        tooltipClass: styles.stepsTooltipClass,
        highlightClass: styles.stepsHighlight2Class,
      },
      // {
      //   element: '#MLModifyTraining',
      //   intro: intl.formatMessage({ id: 'machineLearning.tutorial.step3' }),
      //   position: 'bottom',
      //   tooltipClass: styles.stepsTooltipClass,
      //   highlightClass: styles.stepsHighlight2Class,
      // },
    ],
  };

  const stepsOnBeforeExit = (stepIndex) => {
    if (stepIndex !== steps.props.steps.length - 1) {
      return false;
    } else {
      return true;
    }
  };

  const stepsOnExit = (stepIndex) => {
    setStepsEnabled(false);
    sessionStorage.setItem('hideToolTipML', true);
    return true;
  };

  const onToggleChange = async (e) => {
    if (machineLearning.current?.active) {
      onToggleModeBlocks(e);
    } else {
      await onToggleModeAI(e);
    }
  };

  const onToggleModeAI = async (e) => {
    if (props.onChange) {
      props.onChange(e);
    }
    dispatch(setMLActive(true));
    if (machineLearning.current.trainingStep === TRAIN_STEPS.CLASSIFY) {
      if (isClassifying) {
        await startMedia();
      }
    }
  };

  const onToggleModeBlocks = (e) => {
    if (props.onChange) {
      props.onChange(e);
    }
    dispatch(setMLActive(false));
    stopMedia();
  };

  const changeTrainingStep = (newStep) => {
    setErrorMessage(null);
    dispatch(setMLIsClassifying(false));
    if (!loading) {
      dispatch(setMLTrainingStep(newStep));
    }
  };

  const handleTrainingParams = (newParams) => {
    const sanitized = { ...newParams };
    if (sanitized.epochs !== undefined && sanitized.epochs < 1) sanitized.epochs = 1;
    if (sanitized.fineTunningEpochs !== undefined && sanitized.fineTunningEpochs < 1) sanitized.fineTunningEpochs = 1;
    if (sanitized.learningRate !== undefined && sanitized.learningRate <= 0) sanitized.learningRate = 0.0001;
    if (sanitized.batchSize !== undefined && sanitized.batchSize < 1) sanitized.batchSize = 1;
    setModelTrained(false);
    setTrainingParams(sanitized);
  };

  const handleAskCameraPermissions = () => {
    startMedia()
      .then(() => {
        setCameraAllowed(true);
      })
      .catch((e) => {
        console.error('=============', e);
        setCameraAllowed(false);
      });
  };

  const startDataSet = async () => {
    setCameraAllowed(true);
  };

  const handleTrain = () => {
    changeTrainingStep(TRAIN_STEPS.TRAIN);
    setLoading('Preparando modelo ...');
    // plotterData.current = [];
    trainOnData();
  };

  const trainOnData = async () => {
    dispatch(setMLIsTraining(true));
    dispatch(setMLModelTrained(false));
    setModelTrained(false);
    await train({
      onTrainBegin: async (logs, title = null) => {
        const titleDiv = document.getElementById('plot-title');
        titleDiv.innerHTML = title ? title : '';
        // console.log("train begin: ", logs, title);
      },
      onTrainEnd: async (logs) => {
        // console.log("train end: ", logs);
      },
      onBatchEnd: async (batch, logs) => {
        // console.log("batch end: ", batch, logs);
      },
      onEpochBegin: async (epoch, logs) => {
        if (epoch === 0) {
          const trainResults = {
            epoch: 0,
            acc: 0,
            loss: 0,
            val_acc: 0,
            val_loss: 0,
          };
          setTrainingData(trainResults);
          if (plotterData.current) {
            plotterData.current = [];
            plotterData.current.push(trainResults);
          }
        }
        // console.log("Epoch begin: ", epoch, logs);
      },
      onEpochEnd: async (epoch, log) => {
        // console.log("Epoch end: ", epoch, log);
        const trainResults = {
          epoch,
          acc: log.acc || 0,
          loss: log.loss || 0,
          val_acc: log.val_acc || 0,
          val_loss: log.val_loss || 0,
        };
        setTrainingData(trainResults);
        if (plotterData.current) {
          plotterData.current.push(trainResults);
        }
      },
    });
    const model = await saveModelToDB();
    dispatch(setMLModelWeights(model.weightsBase64 || null));
    dispatch(setMLModelJson(model.json || null));
    dispatch(setMLIsTraining(false));
    setTimeout(() => {
      dispatch(setMLModelTrained(true));
      setModelTrained(true);
      setLoading(null);
    }, 500);
  };

  const setDefaultClasses = async () => {
    if (Object.keys(machineLearning.current.samples).length <= 0) {
      dispatch(setMLSamples(getSamples() || {}));
    } else {
      if (machineLearning.current.modelJson && machineLearning.current.modelWeights) {
        await loadModelFromDB(machineLearning.current.modelJson, machineLearning.current.modelWeights);
        // if (machineLearning.current.trainingStep === TRAIN_STEPS.CLASSIFY) {
        //   startClassifyingProcess();
        // }
      }
      importSamples(machineLearning.current.samples || {});
      if (modelType === MODEL_TYPES.SOUNDS) {
        dispatch(deleteMLTensors());
      }
      setModelTrained(machineLearning.current.modelTrained || false);
      changeTrainingStep(machineLearning.current.trainingStep || TRAIN_STEPS.TEACH);
      if (machineLearning.current.samplesModalActive) {
        openSamplesDialog(machineLearning.current.samplesModalActive);
      }
    }
  };

  const startClassifyingProcess = async () => {
    startClassifying();
    dispatch(setMLIsClassifying(true));
    await startMedia();
  };

  const removeClass = (id) => {
    deleteClass(id);
    dispatch(setMLSamples(getSamples()));
    dispatch(setMLModelTrained(false));
    setModelTrained(false);
  };

  const hideClass = (index) => {
    const samples = getSamples();
    if (samples[index]) {
      samples[index].active = false;
      dispatch(setMLSamples({ ...samples }));
      dispatch(setMLModelTrained(false));
      setModelTrained(false);
    }
  };

  const showClass = (index) => {
    const samples = getSamples();
    if (samples[index]) {
      samples[index].active = true;
      dispatch(setMLSamples({ ...samples }));
      dispatch(setMLModelTrained(false));
      setModelTrained(false);
    }
  };

  const getExistingClassNames = (excludeIndex = null) => {
    return Object.entries(machineLearning.current.samples)
      .filter(([key]) => key !== excludeIndex)
      .map(([, sample]) => (sample.name || '').trim().toLowerCase())
      .filter((name) => name !== '');
  };

  const updateClassName = (index, newName) => {
    const trimmed = newName.trim();
    const existingNames = getExistingClassNames(index);
    if (trimmed !== '' && existingNames.includes(trimmed.toLowerCase())) {
      return false;
    }
    editClassName(index, newName);
    dispatch(setMLSamples(getSamples()));
    return true;
  };

  const addEmptyClass = () => {
    setErrorMessage(null);
    addClass();
    dispatch(setMLSamples(getSamples()));
    dispatch(setMLModelTrained(false));
    setModelTrained(false);
  };

  const openSamplesDialog = async (id) => {
    setCurrentSampleId(id);
    setShowSamplesDialog(true);
    dispatch(setMLSamplesModalActive(id));
    await startMedia();
  };

  const closeSamplesDialog = () => {
    setShowSamplesDialog(false);
    dispatch(setMLSamplesModalActive(null));
    stopMedia();
  };

  const handleOnLoadSamples = () => {
    setShowSamplesDialog(false) | dispatch(setMLSamplesModalActive(null));
    stopMedia();
  };

  const handleOnTakeSample = async (_, text, audioBuffer) => {
    if (currentSampleId) {
      dispatch(setMLModelTrained(false));
      setModelTrained(false);
      setLoadingSample(true);
      const count = await addSample(currentSampleId, text, audioBuffer);
      dispatch(setMLSamples(getSamples() || {}));
      setLoadingSample(false);
    }
  };

  const handleDeleteSampleImages = (indexes) => {
    if (currentSampleId) {
      dispatch(setMLModelTrained(false));
      setModelTrained(false);
      deleteSampleImages(currentSampleId, indexes);
      dispatch(setMLSamples(getSamples() || {}));
    }
  };

  const renderClassContainers = () => {
    const containers = [];
    const className = machineLearning.current.samples[currentSampleId]?.nonEditable
      ? intl.formatMessage({ id: machineLearning.current.samples[currentSampleId]?.name })
      : machineLearning.current.samples[currentSampleId]?.name;
    containers.push(
      <MLSamplesDialog
        key="ml-samples-dialog"
        isCreabots={isCreabots}
        showBackdrop={true}
        open={showSamplesDialog}
        type={'normal'}
        onClose={closeSamplesDialog}
        samples={machineLearning.current.samples[currentSampleId]?.image || []}
        className={className || 'Clase'}
        onTakeSample={handleOnTakeSample}
        onLoadSamples={handleOnLoadSamples}
        onDeleteSampleImages={handleDeleteSampleImages}
        loadingSample={loadingSample}
        modelType={modelType}
        minSamplesRequired={minSamplesRequired}
        mediaWrapper={mediaWrapper}
      />
    );
    Object.keys(machineLearning.current.samples).map((index, keyIndex) => {
      containers.push(
        <MLClassContainer
          key={`ml-class-container-${index}`}
          keyIndex={keyIndex}
          sample={machineLearning.current.samples[index]}
          loading={loading}
          onEditClassName={updateClassName}
          existingNames={getExistingClassNames(index)}
          index={index}
          onDeleteClass={removeClass}
          openSamplesDialog={openSamplesDialog}
          onHideClass={hideClass}
          onShowClass={showClass}
          isCreabots={isCreabots}
          darktheme={props.darktheme || false}
        />
      );
    });
    containers.push(
      <div className={styles.addClassButtonContainer} key="add-class-button-container">
        <Button
          className={styles.addClassButton}
          id="add-class-btn"
          key="add-class-button"
          onClick={() => addEmptyClass()}
          disabled={loading}
        >
          <PlusIcon />
          {intl.formatMessage({ id: 'machineLearning.mlPanel.addClass' })}
        </Button>
      </div>
    );

    return containers;
  };

  const renderTrainContainer = () => {
    const containers = [];
    // if (!isCreabots) {
    //   containers.push(
    //     <>
    //       <div><strong>{intl.formatMessage({ id: 'machineLearning.mlPanel.trainingParams.title' })}:</strong></div>
    //       <div className={styles.trainParams}>
    //         {trainingParams.epochs >= 0 ? (
    //           <div><label>{intl.formatMessage({ id: 'machineLearning.mlPanel.trainingParams.epochs' })}:</label><input type="number" value={trainingParams.epochs} onChange={(e) => handleTrainingParams({ ...trainingParams, epochs: e.target.value })} /></div>
    //         ) : (null)}
    //         {trainingParams.fineTunningEpochs >= 0 ? (
    //           <div><label>{intl.formatMessage({ id: 'machineLearning.mlPanel.trainingParams.fineTunningEpochs' })}:</label><input type="number" value={trainingParams.fineTunningEpochs} onChange={(e) => handleTrainingParams({ ...trainingParams, fineTunningEpochs: e.target.value })} /></div>
    //         ) : (null)}
    //         {trainingParams.learningRate >= 0 ? (
    //           <div className={styles.extraWidth}><label>{intl.formatMessage({ id: 'machineLearning.mlPanel.trainingParams.learningRate' })}:</label><input type="number" value={trainingParams.learningRate} onChange={(e) => handleTrainingParams({ ...trainingParams, learningRate: e.target.value })} /></div>
    //         ) : (null)}
    //         {trainingParams.batchSize >= 0 ? (
    //           <div><label>{intl.formatMessage({ id: 'machineLearning.mlPanel.trainingParams.batchSize' })}:</label><input type="number" value={trainingParams.batchSize} onChange={(e) => handleTrainingParams({ ...trainingParams, batchSize: e.target.value })} /></div>
    //         ) : (null)}
    //       </div>
    //     </>
    //   );
    // }

    containers.push(
      <>
        <div className={styles.plotTitle} id="plot-title"></div>
      </>
    );

    // if (!isCreabots) {
    //   containers.push(
    //     <div className={styles.trainResults} >
    //       <div>{intl.formatMessage({ id: 'machineLearning.mlPanel.epoch' })}: <span>{trainingData.epoch + 1}</span></div>
    //       <div>{intl.formatMessage({ id: 'machineLearning.mlPanel.precision' })}: <span>{trainingData.acc.toFixed(2)}</span></div>
    //       <div>{intl.formatMessage({ id: 'machineLearning.mlPanel.precisionTest' })}: <span>{trainingData.val_acc.toFixed(2)}</span></div>
    //       <div>{intl.formatMessage({ id: 'machineLearning.mlPanel.loss' })}: <span>{trainingData.loss.toFixed(2)}</span></div>
    //       <div>{intl.formatMessage({ id: 'machineLearning.mlPanel.lossTest' })}: <span>{trainingData.val_loss.toFixed(2)}</span></div>
    //     </div >
    //   );
    // }

    containers.push(
      <div>
        {trainingStep === TRAIN_STEPS.TRAIN && plotterData.current && plotterData.current.length > 0 && (
          <div className={styles.graphBox}>
            {plot()}
            <div
              style={{
                width: '100%',
                marginTop: 8,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <LinearProgress
                className={styles.loading}
                variant="determinate"
                value={trainingParams.epochs > 0 ? ((trainingData.epoch + 1) / trainingParams.epochs) * 100 : 0}
                style={{ width: '75%', marginLeft: 12 }}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: 'lightgray',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                    backgroundColor: '#735FE3',
                  },
                }}
              />
              <span className={styles.percentage}>
                {trainingParams.epochs > 0 ? Math.round(((trainingData.epoch + 1) / trainingParams.epochs) * 100) : 0}%
              </span>
            </div>
          </div>
        )}

        {/* {!modelTrained && !loading && ( */}
        {!loading &&
          plotterData.current.length === 0 &&
          (isFreeProject && isTrainingDisabled ? (
            <div>
              <div className={styles.deactivatedTrainActionsColumn}>
                <LockIcon />
                <span className={styles.deactivatedTitle}>
                  {intl.formatMessage({ id: 'machineLearning.mlPanel.freeProject.cannotTrain' })}
                </span>
                <span className={styles.deactivatedDescription}>
                  {intl.formatMessage({ id: 'machineLearning.mlPanel.freeProject.needMoreClasses' })}
                </span>
                <Button className={styles.goToTrainButton} onClick={() => changeTrainingStep(TRAIN_STEPS.TEACH)}>
                  <ArrowLeftIcon />
                  {intl.formatMessage({ id: 'machineLearning.mlPanel.freeProject.goToClasses' })}
                </Button>
              </div>
            </div>
          ) : (
            <div className={styles.trainActionsButtons}>
              <div className={styles.trainActionsColumn}>
                <Button
                  className={styles.trainActionButton}
                  id="train-btn"
                  onClick={() => handleTrain()}
                  disabled={loading}
                >
                  <PlayIcon color={'var(--text-brand-primary)'} />
                </Button>
                <span className={styles.trainTitle}>{intl.formatMessage({ id: 'machineLearning.mlPanel.title' })}</span>
                <span className={styles.trainDescription}>
                  {intl.formatMessage({ id: 'machineLearning.mlPanel.description' })}
                </span>
              </div>
            </div>
          ))}
      </div>
    );
    return containers;
  };

  const renderPredictionContainers = () => {
    const containers = [];
    containers.push(
      <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
        <div className={styles.mediaWrapper} id={mediaWrapper}>
          <canvas></canvas>
          <video playsInline />
        </div>
      </div>
    );

    const getBarColor = (val) => {
      if (val <= 33) return '#F45C3A';
      if (val <= 66) return '#FFB800';
      return '#50D174';
    };

    Object.keys(machineLearning.current.samples).forEach((key) => {
      const sample = machineLearning.current.samples[key];
      if (!sample.active) return;
      const value = confidences[sample.name] || confidences[key] || 0;
      const percentage = parseFloat((value * 100).toFixed(2) || 0);
      containers.push(
        <div key={`class-${sample.index}`} className={styles.classContainer}>
          <div className={styles.label}>
            <label className={styles.className}>
              {sample.nonEditable ? intl.formatMessage({ id: sample.name }) : sample.name}
            </label>
            <span className={styles.confidence}>{parseInt(percentage, 10) || 0} %</span>
          </div>
          <LinearProgress
            className={styles.progress}
            variant="determinate"
            value={percentage}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: 'var(--color-background-hover)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 5,
                backgroundColor: getBarColor(percentage),
              },
            }}
          />
        </div>
      );
    });
    return containers;
  };

  const plot = () => {
    const precision = [];
    const precisionTest = [];
    const loss = [];
    const lossTest = [];
    let maxLoss = 0;
    if (plotterData.current) {
      plotterData.current.map((data) => {
        precision.push({ x: data.epoch, y: data.acc });
        precisionTest.push({ x: data.epoch, y: data.val_acc });
        loss.push({ x: data.epoch, y: data.loss });
        lossTest.push({ x: data.epoch, y: data.val_loss });
        if (data.loss > maxLoss) {
          maxLoss = data.loss;
        }
        if (data.val_loss > maxLoss) {
          maxLoss = data.val_loss;
        }
      });
    }

    return precision.length === 0 ? null : (
      <div className={styles.graph}>
        <FlexibleXYPlot yDomain={[0, 1]} height={180}>
          {/* <DiscreteColorLegend
            items={[
              { title: intl.formatMessage({ id: 'machineLearning.mlPanel.precision' }), color: "#6ad0ff", strokeWidth: 6 },
              { title: intl.formatMessage({ id: 'machineLearning.mlPanel.precisionTest' }), color: "#157dad", strokeWidth: 6 },
            ]}
            orientation="horizontal"
            style={{ fontColor: '#fff' }}
          /> */}
          <VerticalGridLines style={{ opacity: 1, strokeDasharray: '4 4' }} />
          <HorizontalGridLines style={{ opacity: 0.5 }} />
          <XAxis style={{ text: { fill: 'var(--color-placeholder)' } }} />
          <YAxis style={{ text: { fill: 'var(--color-placeholder)' } }} />
          <LineSeries
            color="#4326D9"
            key={`${Math.random()}`}
            data={precision}
            style={{ strokeWidth: 2, mark: { stroke: 'white' } }}
          />
          {/* <LineSeries
            color="#4326D9"
            key={`${Math.random()}`}
            data={precisionTest}
            style={{ mark: { stroke: 'white' } }}
          /> */}
        </FlexibleXYPlot>
        {/* {!isCreabots ? (
          <FlexibleXYPlot yDomain={[0, maxLoss]} height={180}>
            <DiscreteColorLegend
              items={[
                { title: intl.formatMessage({ id: 'machineLearning.mlPanel.loss' }), color: "#FF9833", strokeWidth: 6 },
                { title: intl.formatMessage({ id: 'machineLearning.mlPanel.lossTest' }), color: "#CC3333", strokeWidth: 6 },
              ]}
              orientation="horizontal"
            />
            <VerticalGridLines style={{ opacity: 0.2 }} />
            <HorizontalGridLines style={{ opacity: 0.2 }} />
            <XAxis />
            <YAxis />
            <LineSeries
              color="#FF9833"
              key={`${Math.random()}`}
              data={loss}
              style={{ strokeWidth: 2, mark: { stroke: 'white' } }}
            />
            <LineSeries
              color="#CC3333"
              key={`${Math.random()}`}
              data={lossTest}
              style={{ mark: { stroke: 'white' } }}
            />
          </FlexibleXYPlot>
        ) : null} */}
      </div>
    );
  };

  useEffect(() => {
    if (isTrainingDisabled || !modelTrained) {
      plotterData.current = [];
    }
  }, [isTrainingDisabled, modelTrained]);

  useEffect(() => {
    if (mlHandler && props.setMLHandler) {
      props.setMLHandler(mlHandler);
    }
  }, [mlHandler]);

  useEffect(async () => {
    setLoading('Preparando todo ...');
    if (modelType) {
      try {
        const currentMLHandler = await init(modelType);
        const newTrainingParams = getDefaultTrainingParams();
        setTrainingParams(newTrainingParams);
        dispatch(setMLTrainingParams(newTrainingParams));
        const minSamples = getMinSamplesRequired();
        setMinSamplesRequired(minSamples);
        dispatch(setMLMinSamplesRequired(minSamples));
        startDataSet();
        setErrorMessage(null);
        setDefaultClasses();
        setMLHandler(currentMLHandler);
      } catch (e) {
        console.error('=============', e);
        setErrorMessage(intl.formatMessage({ id: 'machineLearning.mlPanel.cameraPermissionDenied' }));
        setCameraAllowed(false);
        setLoading(null);
      }
    }
    if (modelType && props.iaModelJson && props.iaModelBin) {
      dispatch(setMLSamples({}));
      clearAllSamples();
      setImportedModel(props.iaModelJson);
      const importedModelClasses = await loadModel(props.iaModelJson, props.iaModelBin);
      if (importedModelClasses) {
        const classes = Object.keys(importedModelClasses);
        for (let i = 0; i < classes.length; i++) {
          addClass(classes[i]);
        }
        dispatch(setMLSamples(getSamples()));
        changeTrainingStep(TRAIN_STEPS.CLASSIFY);
        startClassifyingProcess();
      } else {
        setImportedModel(null);
        setDefaultClasses();
        setErrorMessage(intl.formatMessage({ id: 'machineLearning.mlPanel.errors.modelFiledLoading' }));
      }
      if (props.onFinishLoading) {
        props.onFinishLoading();
      }
    }
    setLoading(null);

    return () => {
      stopMedia();
      setLoading(null);
    };
  }, [modelType, props.iaModelJson, props.iaModelBin]);

  useEffect(async () => {
    if (modelType && !machineLearning.current?.active) {
      stopMedia();
    }
  }, [modelType, machineLearning.current?.active]);

  useEffect(() => {
    if (props.workspace) {
      const classes = {};
      for (const sample of Object.values(machineLearning.current.samples)) {
        if (sample.active) {
          classes[`${commandClass}${sample.index}`] = sample.nonEditable
            ? intl.formatMessage({ id: sample.name })
            : sample.name;
        }
      }
      classes[stopCommandClass] = intl.formatMessage({ id: 'machineLearning.mlPanel.errors.notRecognized' });
      props.workspace.setMLClasses(classes);
    }
  }, [props.workspace, machineLearning.current.samples]);

  useEffect(() => {
    const hideToolTipML = sessionStorage.getItem('hideToolTipML');
    if (props.workspace && importedModel && !hideToolTipML) {
      setStepsEnabled(true);
    }
  }, [props.workspace, importedModel]);

  useEffect(async () => {
    if (props.iaModel) {
      const model = props.iaModel || MODEL_TYPES.HANDS;
      setModelType(model);
      dispatch(setMLModel(model));
    }
  }, [props.iaModel]);

  // TUTORIAL: Check step 1
  useEffect(async () => {
    const code = blocklyState?.project?.code || null;
    if (stepsEnabled && !isCreabots && code && currentStep === 0) {
      if (new RegExp('if\\(BT1\\.available\\(\\)\\)\\{', 'i').test(code.replace(/\n|\s/gi, ''))) {
        steps.introJs.nextStep();
        setCurrentStep(1);
      }
    }
  }, [blocklyState?.project?.code]);

  // TUTORIAL: Check step 2
  useEffect(() => {
    if (
      stepsEnabled &&
      !isCreabots &&
      currentStep === 1 &&
      blocklyState?.build?.building &&
      (blocklyState?.build?.port !== '' || blocklyState?.build?.microbitForceDownload)
    ) {
      steps.introJs.nextStep();
      setCurrentStep(2);
    }
  }, [blocklyState?.build?.building]);

  // TUTORIAL: Check step 3
  useEffect(() => {
    lastSentCommand.current = '';
    if (stepsEnabled && !isCreabots && connectedRef.current && currentStep === 2) {
      setTimeout(() => {
        if (steps.props.steps.length > 3) {
          steps.introJs.nextStep();
          setCurrentStep(3);
          setTimeout(() => {
            steps.introJs.exit();
          }, 30000);
        } else {
          steps.introJs.exit();
        }
      }, 1);
    }
  }, [connectedRef.current]);

  useEffect(async () => {
    if (codeUploaded && connectedRef.current) {
      await startNotifications();
      lastSentCommand.current = '';
    }
  }, [codeUploaded]);

  useEffect(async () => {
    if (machineLearning.current?.trainingStep) {
      const newStep = machineLearning.current.trainingStep;
      if (newStep === TRAIN_STEPS.CLASSIFY && importedModel && !isCreabots) return;
      stopMedia();
      stopClassifying();
      dispatch(setMLIsClassifying(false));

      if (newStep === TRAIN_STEPS.TEACH) {
        if (modelType === MODEL_TYPES.TEXTS && mediaWrapper) {
          const wrapper = document.getElementById(mediaWrapper);
          wrapper ? (wrapper.innerHTML = '') : null;
        }
        // stopClassifying();
      }
    }
  }, [machineLearning.current?.trainingStep]);

  const modalClass = `${styles.mlFloatingModal} ${!isCreabots && !props.darktheme ? 'theme-aula' : ''} ${!isCreabots ? styles.codeMLBox : ''
    } ${machineLearning.current.active ? styles.active : ''}`;
  // const modalClass = isCreabots ? `${styles.mlFloatingModal} ${(machineLearning.current.active) ? styles.active : ''}` : `${styles.codeMLBox} ${(machineLearning.current.active) ? styles.active : ''} ${(props.className || '')}`;
  const modalBackdropClass = `${styles.mlFloatingModalBackdrop} ${isCreabots && machineLearning.current.active ? styles.visible : ''
    }`;

  return (
    <React.Fragment>
      <div className={modalBackdropClass} />
      <div className={modalClass}>
        {props.iaModel && (
          <div key="contentCodeBox" className={styles.contentCodeBox}>
            <div key="contentTop" className={styles.contentTop}>
              <div className={styles.title}>{intl.formatMessage({ id: 'machineLearning.title' })}</div>
              <div key="ml-tutorial-header" className={styles.header}>
                <div className={styles.menu}>
                  {importedModel && (
                    <button
                      id="MLModifyTraining"
                      className={clsx(styles.activeButton, styles.backToTrainButton)}
                      onClick={() => {
                        const baseUrl = `https://${routes.trainUrl}/models?model=`;
                        window.location.href = baseUrl + modelType;
                      }}
                    >
                      <img src="/images/ai/backToTraining.svg" height={20} alt="Back to training" />
                      {intl.formatMessage({ id: 'machineLearning.buttons.backToTraining' })}
                    </button>
                  )}
                  {errorMessage && (
                    <div className={styles.errorMessage}>
                      {errorMessage}
                    </div>
                  )}
                  <div className={styles.stepButtons}>
                    <button
                      id="ml-teach"
                      className={clsx(styles.buttons, trainingStep === TRAIN_STEPS.TEACH && styles.activeButton)}
                      onClick={() => changeTrainingStep(TRAIN_STEPS.TEACH)}
                      disabled={importedModel}
                    >
                      {intl.formatMessage({ id: 'machineLearning.mlPanel.menu.loadSamples' })}
                    </button>
                    <button
                      id="ml-train"
                      className={clsx(styles.buttons, trainingStep === TRAIN_STEPS.TRAIN && styles.activeButton)}
                      onClick={() => changeTrainingStep(TRAIN_STEPS.TRAIN)}
                      disabled={!isFreeProject && isTrainingDisabled}
                    >
                      {intl.formatMessage({ id: 'machineLearning.mlPanel.menu.trainModel' })}
                    </button>
                    <button
                      id="ml-analyze"
                      className={clsx(styles.buttons, trainingStep === TRAIN_STEPS.CLASSIFY && styles.activeButton)}
                      onClick={() => changeTrainingStep(TRAIN_STEPS.CLASSIFY)}
                      disabled={!isFreeProject && !modelTrained}
                    >
                      {intl.formatMessage({ id: 'machineLearning.mlPanel.menu.analyzeModel' })}
                    </button>
                  </div>
                </div>
              </div>

              {trainingStep === TRAIN_STEPS.TEACH ? renderClassContainers() : null}

              {trainingStep === TRAIN_STEPS.TRAIN ? (
                <div key="ml-tutorial-train-actions" className={styles.trainActions}>
                  {renderTrainContainer()}
                </div>
              ) : null}

              {trainingStep === TRAIN_STEPS.CLASSIFY ? (
                <div key="ml-tutorial-classify-actions" className={styles.classifyActions}>
                  {!isClassifying ? (
                    isFreeProject && !modelTrained ? (
                      <div>
                        <div className={styles.deactivatedTrainActionsColumn}>
                          <LockIcon />
                          <span className={styles.deactivatedTitle}>
                            {intl.formatMessage({ id: 'machineLearning.mlPanel.freeProject.cannotEvaluate' })}
                          </span>
                          <span className={styles.deactivatedDescription}>
                            {intl.formatMessage({ id: 'machineLearning.mlPanel.freeProject.trainFirst' })}
                          </span>
                          <Button
                            className={styles.goToTrainButton}
                            onClick={() => changeTrainingStep(TRAIN_STEPS.TRAIN)}
                          >
                            <ArrowLeftIcon />
                            {intl.formatMessage({ id: 'machineLearning.mlPanel.freeProject.goToTrain' })}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        className={styles.trainActionsButtons}
                        id="analyze-btn"
                        onClick={() => startClassifyingProcess()}
                        disabled={loading}
                      >
                        <div className={styles.trainActionsColumn}>
                          <CamaraAnalyze color={'var(--text-brand-primary)'} />
                          <span className={styles.trainTitle}>
                            {intl.formatMessage({ id: 'machineLearning.mlPanel.preview.title' })}
                          </span>
                          <span className={styles.trainDescription}>
                            {intl.formatMessage({ id: 'machineLearning.mlPanel.preview.description' })}
                          </span>
                        </div>
                      </button>
                    )
                  ) : (
                    <>
                      {renderPredictionContainers()}
                      <div className={styles.bluetooth}>
                        {!connectedRef.current && hasBluetooth ? (
                          <>
                            <button
                              className={styles.bluetoothButton}
                              id="MLConnectBluetooth"
                              onClick={requestConnection}
                            >
                              <Bluetooth color={'var(--color-placeholder)'} />
                              <span className={styles.bluetoothButtonText}>
                                {intl.formatMessage({ id: 'machineLearning.buttons.connectBluetooth' })}{' '}
                              </span>
                              <ArrowIcon color={'var(--color-placeholder)'} />
                            </button>
                          </>
                        ) : (
                          <button
                            className={styles.bluetoothContectedButton}
                            id="MLConnectBluetooth"
                            onClick={disconnect}
                          >
                            <BluetoothConected color={'var(--color-placeholder)'} />
                            <span className={styles.bluetoothConectedButtonText}>
                              {intl.formatMessage({ id: 'machineLearning.buttons.disconnectBluetooth' })}
                            </span>
                            <ArrowIcon color={'var(--color-placeholder)'} />
                          </button>
                        )}
                      </div>
                      {stepsEnabled && !isCreabots ? (
                        <Steps
                          key="ml-tutorial-steps"
                          ref={(steps) => setSteps(steps)}
                          enabled={Boolean(tutorialOptions.steps.length)}
                          initialStep={0}
                          steps={tutorialOptions.steps}
                          onExit={stepsOnExit}
                          onBeforeExit={stepsOnBeforeExit}
                          options={tutorialOptions.options}
                        />
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}

        {isCreabots ? (
          <div key="contentToggle" className={styles.toggle} role="button" tabIndex="0">
            {props.iaModel && (
              <AdvancedTooltip
                title={getAIPanelTooltipConfig().title}
                description={getAIPanelTooltipConfig().description}
                icon={getAIPanelIcon()}
                iconClass="toggleAIActive"
                tooltipId={getAIPanelTooltipConfig().tooltipId}
                open={codeUploaded && isFreeProject}
                placement="right"
              >
                <div
                  id="toggleAI"
                  className={clsx(styles.toggleAI, machineLearning.current.active && styles.active)}
                  onClick={onToggleModeAI}
                >
                  {machineLearning.current.active ? <GeminiFill /> : <GeminiIcon />}
                </div>
              </AdvancedTooltip>
            )}
            <AdvancedTooltip
              title={TOOLTIP_CONFIG.BLOCKS_PANEL.title}
              description={TOOLTIP_CONFIG.BLOCKS_PANEL.description}
              icon={<BlocksFill width={14} height={14} />}
              iconClass="toggleBlocksActive"
              tooltipId={TOOLTIP_CONFIG.BLOCKS_PANEL.tooltipId}
              open={trainingStep === TRAIN_STEPS.CLASSIFY && isClassifying && isFreeProject}
              placement="right"
            >
              <div
                id="toggleBlocks"
                className={clsx(styles.toggleBlocks, !machineLearning.current.active && styles.active)}
                onClick={onToggleModeBlocks}
              >
                {!machineLearning.current.active ? <BlocksFill /> : <IconBlocks />}
              </div>
            </AdvancedTooltip>
          </div>
        ) : (
          <div key="contentToggle" className={styles.mlToggle} role="button" tabIndex="0" onClick={onToggleChange}>
            <span className={styles.curlyBraces}>
              <img src={`${process.env.IS_ELECTRON ? '.' : ''}/images/ai/toggleAiPanel.svg`} />
            </span>
            <span className={`icon-chevron-left ${styles.iconChevronLeft} ${props.active ? styles.rotateArrow : ''}`} />
          </div>
        )}
      </div>
    </React.Fragment>
  );
});

export default MLPanel;
